/**
 * Design project document (design tool v2, `/thiet-ke/bo-tri/`). Single source of truth of a rooftop design:
 * PLAN coordinates in millimetres (x → right of the drawing, y → down the drawing; the top of the drawing points to
 * `site.upBearingDeg`). Panels are not stored: they are derived from each roof's layout settings (+ removed slots).
 *
 * Saved as a JSON file (`S.M.A.R.T.solar-project` v3), in browser autosave, and inside the server draft as
 * `draft.layout.project` next to the px-based layout read by the engineering / BOQ pipeline.
 *
 * v3 (additive, v2 files are migrated on read): design workspace, roofs laid out module-by-module (MANUAL mode with
 * stored modules), carport canopies, BESS site layout, installation zones (`pvZones`, roof `layout.fill`).
 */
import { z } from 'zod';
import { EDGE_KINDS } from './array-layout';
import { buildingSchema } from './structure/building';
import { CABLE_ROUTE_KINDS, EQUIPMENT_KINDS, structureInstanceSchema } from './structure/schema';
import { steelSectionSchema } from './structure/sections';
import { bessDataSchema, emptyBess } from './bess';
import { emptyOm, omDataSchema } from './om';
import { OBSTACLE_KINDS } from './schemas';

export const PROJECT_SCHEMA = 'S.M.A.R.T.solar-project';
export const PROJECT_VERSION = 3;

export const WORKSPACES = ['RESIDENTIAL', 'CI', 'BESS'] as const;
export type Workspace = (typeof WORKSPACES)[number];
export const WORKSPACE_LABEL: Record<Workspace, string> = {
  RESIDENTIAL: 'Thiết kế Solar dân dụng',
  CI: 'Thiết kế Solar C&I',
  BESS: 'Thiết kế BESS',
};

const pt = z.tuple([z.number().finite(), z.number().finite()]);
const id = z.string().min(1).max(40);
const mm = (min: number, max: number) => z.number().min(min).max(max);

export const ROOF_KINDS = ['METAL', 'STANDING_SEAM', 'TILE', 'CONCRETE'] as const;
export const ROOF_KIND_LABEL: Record<(typeof ROOF_KINDS)[number], string> = {
  METAL: 'Mái tôn',
  STANDING_SEAM: 'Mái tôn đứng (seam)',
  TILE: 'Mái ngói',
  CONCRETE: 'Mái bằng bê tông',
};

export const arraySettingsSchema = z
  .object({
    enabled: z.boolean().default(true),
    orientation: z.enum(['PORTRAIT', 'LANDSCAPE', 'AUTO']).default('PORTRAIT'),
    /** Row direction: drawing axis, longest edge, the roof's eave, or edge n */
    align: z
      .string()
      .regex(/^(AXIS|LONGEST|EAVE|E\d{1,3})$/)
      .default('EAVE'),
    /** Gap between panels in a row (null ⇒ module gap) */
    colGapMm: mm(0, 2000).nullable().default(null),
    /** Gap between rows (null ⇒ module gap) — larger for tilted / elevated rows */
    rowGapMm: mm(0, 20_000).nullable().default(null),
    /** Setback by edge kind, overridable per edge index */
    setbackByKind: z.partialRecord(z.enum(EDGE_KINDS), mm(0, 20_000)).default({}),
    setbackByEdge: z.record(z.string().regex(/^\d{1,3}$/), mm(0, 20_000)).default({}),
    aisles: z
      .object({
        everyRows: z.number().int().min(0).max(200).default(0),
        rowAisleMm: mm(0, 10_000).default(800),
        everyCols: z.number().int().min(0).max(500).default(0),
        colAisleMm: mm(0, 10_000).default(800),
      })
      .default({ everyRows: 0, rowAisleMm: 800, everyCols: 0, colAisleMm: 800 }),
    /** Grid phase (roof-plane mm, row frame); null ⇒ automatic */
    offset: z.object({ u: z.number().finite(), v: z.number().finite() }).nullable().default(null),
    /** Slots removed by the user */
    excluded: z.array(z.string().max(40)).max(50_000).default([]),
    /** AUTO = grid from the settings; MANUAL = modules stored one by one (`roof.modules`) */
    mode: z.enum(['AUTO', 'MANUAL']).default('AUTO'),
    /**
     * AUTO roofs: ROOF = fill the whole roof (files saved before installation zones); ZONES = panels only inside the
     * installation zones drawn on the roof (`project.pvZones`). New roofs drawn in the tool use ZONES.
     */
    fill: z.enum(['ROOF', 'ZONES']).default('ROOF'),
  })
  .loose();
export type ArraySettings = z.infer<typeof arraySettingsSchema>;

/**
 * A module placed individually (MANUAL roofs). The centre is stored in PLAN mm so roof edits never move it; its
 * footprint is the true module rectangle on the roof plane (rotated by `rotDeg` in that plane) projected to plan.
 */
export const placedModuleSchema = z
  .object({
    id,
    at: pt,
    /** In-plane rotation from the roof's row direction (deg) */
    rotDeg: z.number().min(-360).max(360).default(0),
    orientation: z.enum(['PORTRAIT', 'LANDSCAPE']).default('PORTRAIT'),
    groupId: z.string().max(40).nullable().default(null),
  })
  .loose();
export type PlacedModule = z.infer<typeof placedModuleSchema>;

export const roofSchema = z
  .object({
    id,
    name: z.string().trim().min(1).max(80),
    outline: z.array(pt).min(3).max(500),
    kind: z.enum(ROOF_KINDS).default('METAL'),
    pitchDeg: mm(0, 60),
    /** Azimuth the roof faces (down-slope), deg from North clockwise */
    faceDeg: mm(0, 360),
    /** Roof-top elevation at the low edge (eave), mm */
    eaveMm: mm(0, 200_000),
    /** Edge kind for each edge i (outline[i] → outline[i+1]) */
    edges: z.array(z.enum(EDGE_KINDS)).max(500).default([]),
    buildingId: z.string().max(40).nullable().default(null),
    plane: z.number().int().min(0).max(999).nullable().default(null),
    /** Canopy plane of a carport structure (structure id) — geometry driven by the carport parameters */
    carportId: z.string().max(40).nullable().default(null),
    layout: arraySettingsSchema.default(() => arraySettingsSchema.parse({})),
    /** Modules of a MANUAL roof */
    modules: z.array(placedModuleSchema).max(20_000).default([]),
  })
  .loose();
export type Roof = z.infer<typeof roofSchema>;

/**
 * Installation zone drawn on a roof (PLAN mm). Panels of a ZONES roof are laid out inside each zone with the roof's
 * settings (row direction, orientation, gaps, edge setbacks, exclusions), split into arrays of at most
 * `maxRows` × `maxCols` panels separated by aisles (0 = no limit).
 */
export const pvZoneSchema = z
  .object({
    id,
    roofId: id,
    label: z.string().max(100).optional(),
    outline: z.array(pt).min(3).max(200),
    maxRows: z.number().int().min(0).max(200).default(0),
    maxCols: z.number().int().min(0).max(500).default(0),
    /** Gap between arrays along the slope (between row blocks) / along the rows (between column blocks), mm */
    rowAisleMm: mm(0, 10_000).default(800),
    colAisleMm: mm(0, 10_000).default(800),
    /** Grid phase of the zone (roof-plane mm, row frame); null ⇒ automatic */
    offset: z.object({ u: z.number().finite(), v: z.number().finite() }).nullable().default(null),
    /**
     * Row direction on the PLAN (deg, atan2 of the plan axes) taken from an edge picked on any object — panel edges of
     * the zone run parallel to it. null / absent ⇒ the roof's row direction (or the tilt of the zone's structure).
     */
    rowDirDeg: z.number().min(-360).max(360).nullable().optional(),
    /** Description of the picked edge (display only) */
    rowDirFrom: z.string().max(120).optional(),
  })
  .loose();
export type PvZone = z.infer<typeof pvZoneSchema>;

export const obstacleSchema = z
  .object({
    id,
    kind: z.enum(OBSTACLE_KINDS),
    outline: z.array(pt).min(3).max(200),
    heightMm: mm(0, 30_000).default(600),
    clearanceMm: mm(0, 10_000).default(0),
    label: z.string().max(100).optional(),
  })
  .loose();
export type ProjectObstacle = z.infer<typeof obstacleSchema>;

export const projectEquipmentSchema = z
  .object({
    id,
    kind: z.enum(EQUIPMENT_KINDS),
    label: z.string().trim().min(1).max(80),
    productId: z.uuid().nullable().default(null),
    at: pt,
    mount: z.enum(['ROOF', 'GROUND']).default('GROUND'),
    elevationMm: mm(0, 30_000).default(0),
    sizeMm: z.tuple([mm(10, 20_000), mm(10, 20_000), mm(10, 20_000)]),
    rotationDeg: z.number().min(-360).max(360).default(0),
    /** Free service area in front of the equipment (front = local −y), mm */
    serviceFrontMm: mm(0, 10_000).default(1000),
    serviceSideMm: mm(0, 5000).default(0),
    connectsTo: z.array(z.string().max(40)).max(50).default([]),
  })
  .loose();
export type ProjectEquipment = z.infer<typeof projectEquipmentSchema>;

export const projectRouteSchema = z
  .object({
    id,
    kind: z.enum(CABLE_ROUTE_KINDS),
    label: z.string().max(80).optional(),
    points: z.array(pt).min(2).max(500),
    elevationMm: mm(0, 30_000).default(150),
    widthMm: mm(10, 2000).default(100),
    heightMm: mm(10, 1000).default(50),
    fromId: z.string().max(40).nullable().default(null),
    toId: z.string().max(40).nullable().default(null),
  })
  .loose();
export type ProjectRoute = z.infer<typeof projectRouteSchema>;

/**
 * Neighbouring building / structure (footprint + height, standing on the ground or on a base elevation) or a tree
 * (crown = cylinder of `crownDiameterMm` between `trunkHeightMm` and `heightMm`). Used for shadows only.
 */
export const surroundingSchema = z
  .object({
    id,
    kind: z.enum(['BUILDING', 'TREE']),
    label: z.string().max(80).default(''),
    /** BUILDING: footprint (plan mm) */
    outline: z.array(pt).max(200).default([]),
    /** TREE: trunk position */
    at: pt.nullable().default(null),
    heightMm: mm(100, 300_000),
    baseMm: mm(0, 100_000).default(0),
    crownDiameterMm: mm(200, 50_000).default(4000),
    trunkHeightMm: mm(0, 100_000).default(1500),
  })
  .loose();
export type Surrounding = z.infer<typeof surroundingSchema>;

export const LAYER_KEYS = [
  'background',
  'roofs',
  'panels',
  'obstacles',
  'structures',
  'walkways',
  'safety',
  'access',
  'cleaning',
  'electrical',
  'dimensions',
  'bess',
  'surroundings',
] as const;
export type LayerKey = (typeof LAYER_KEYS)[number];
export const LAYER_LABEL: Record<LayerKey, string> = {
  background: 'Ảnh nền',
  roofs: 'Mái & nhà',
  panels: 'Tấm pin',
  obstacles: 'Vật cản',
  structures: 'Kết cấu đỡ',
  walkways: 'O&M — lối đi',
  safety: 'O&M — an toàn',
  access: 'O&M — tiếp cận & thiết bị',
  cleaning: 'O&M — vệ sinh',
  electrical: 'Điện (máng / tuyến cáp)',
  dimensions: 'Kích thước & đo đạc',
  bess: 'BESS — thiết bị & khu đất',
  surroundings: 'Công trình lân cận & cây xanh',
};

/**
 * Image reference of a background: an inline `data:` URL (files without a server) or a design image stored on the
 * server (`/api/v1/pv-designs/imagery/files/<uuid>` — large imagery is not embedded in the project JSON).
 */
export const DESIGN_IMAGE_URL_RE = /^\/api\/v1\/pv-designs\/imagery\/files\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
export const designImageUrl = (fileId: string) => `/api/v1/pv-designs/imagery/files/${fileId}`;
const imageSrc = z
  .string()
  .max(25_000_000)
  .refine((s) => s.startsWith('data:image/') || DESIGN_IMAGE_URL_RE.test(s), 'Ảnh nền phải là data:image/… hoặc ảnh thiết kế trên máy chủ');

/** Placement of the background on the plan (mm, rotation in degrees, clockwise on the drawing, about the centre). */
const bgPlacement = z.object({ x: z.number().finite(), y: z.number().finite(), w: z.number().positive(), h: z.number().positive(), rotationDeg: z.number().finite().optional() });

/**
 * Perspective calibration of a drone (UAV) photo. Image points are in pixels of the ORIGINAL photo (as displayed,
 * EXIF orientation applied). `h` maps image pixels → calibrated plane (mm), row-major 3 × 3. The rectified texture
 * (`background.src`) is regenerated from the original + calibration, so the photo can be recalibrated any time.
 */
export const DRONE_CALIBRATION_MODES = ['RECT', 'HORIZON', 'NADIR'] as const;
const linePts = z.tuple([pt, pt]);
export const droneCalibrationSchema = z.object({
  mode: z.enum(DRONE_CALIBRATION_MODES),
  /** Horizontal plane the reference marks lie on: the ground, or a flat surface at `planeElevationMm` (flat roof…) */
  plane: z.enum(['GROUND', 'FLAT_ROOF']).default('GROUND'),
  planeElevationMm: mm(-1000_000, 1000_000).default(0),
  /** RECT: 4 image corners of a rectangle on the plane (in order) and its real size */
  quad: z.array(pt).length(4).optional(),
  rectWidthMm: z.number().positive().optional(),
  rectLengthMm: z.number().positive().optional(),
  /** HORIZON: horizon line (marked, or derived from two pairs of parallel edges) + focal length in pixels */
  horizon: linePts.nullable().default(null),
  parallels: z.array(linePts).max(4).default([]),
  focalPx: z.number().positive().optional(),
  /** Known-length reference segment (HORIZON / NADIR: sets the scale) */
  ref: z.object({ a: pt, b: pt, lengthMm: z.number().positive() }).optional(),
  /** Optional check segment: measured vs entered length is reported as calibration error */
  check: z.object({ a: pt, b: pt, lengthMm: z.number().positive() }).optional(),
  /** Ground boundary to rectify (image points) */
  boundary: z.array(pt).min(3).max(8),
  h: z.array(z.number().finite()).length(9),
  imageW: z.number().int().positive(),
  imageH: z.number().int().positive(),
});
export type DroneCalibration = z.infer<typeof droneCalibrationSchema>;

export const backgroundSchema = z.object({
  src: imageSrc,
  x: z.number().finite(),
  y: z.number().finite(),
  w: z.number().positive(),
  h: z.number().positive(),
  /** Rotation about the image centre, degrees clockwise on the drawing (absent = 0). New fields are optional so that
   * older projects / callers stay valid unchanged. */
  rotationDeg: z.number().finite().optional(),
  opacity: z.number().min(0.05).max(1).default(0.6),
  locked: z.boolean().default(false),
  /** Show as a textured ground plane in the 3D view (absent = true) */
  show3d: z.boolean().optional(),
  /** Elevation of the ground plane in 3D (mm, absent = 0; the design keeps its own elevations) */
  elevationMm: mm(-1000_000, 1000_000).optional(),
  /** Where the image came from (absent = FILE) */
  source: z.enum(['FILE', 'MAP', 'DRONE']).optional(),
  /** Server design image (StoredFile id) when `src` is a server URL */
  fileId: z.uuid().optional(),
  /** Placement right after the image was applied ("Đặt lại căn chỉnh") */
  initial: bgPlacement.optional(),
  /** Map imagery: centre & scale it was placed with (north up) and the provider to credit */
  geo: z
    .object({
      lat: z.number().min(-90).max(90),
      lon: z.number().min(-180).max(180),
      zoom: z.number().int().min(0).max(23),
      metersPerPixel: z.number().positive(),
      provider: z.string().max(80),
      providerId: z.string().max(60).optional(),
      /** satellite / aerial imagery vs. an ordinary street map */
      kind: z.enum(['SATELLITE', 'MAP']).optional(),
      attribution: z.string().max(300).optional(),
      capturedAt: z.string().max(40).optional(),
      /** [west, south, east, north] degrees */
      bounds: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
      /** Native resolution of the source as documented by its provider (not the texture resolution) */
      sourceResolution: z.string().max(120).optional(),
    })
    .optional(),
  /** Drone photo: original image + calibration (the rectified texture is `src`) */
  drone: z
    .object({
      originalSrc: imageSrc,
      originalFileId: z.uuid().optional(),
      name: z.string().max(200).default(''),
      exif: z.record(z.string().max(40), z.union([z.string().max(200), z.number()])).default({}),
      calibration: droneCalibrationSchema,
      /** Calibrated plane rectangle covered by the rectified texture (mm, plane coordinates) */
      planeBounds: z.tuple([z.number(), z.number(), z.number(), z.number()]),
    })
    .optional(),
});
export type ProjectBackground = z.infer<typeof backgroundSchema>;

export const projectSchema = z
  .object({
    schema: z.literal(PROJECT_SCHEMA),
    version: z.literal(PROJECT_VERSION),
    id,
    name: z.string().max(200).default(''),
    workspace: z.enum(WORKSPACES).default('RESIDENTIAL'),
    customer: z
      .object({ makh: z.string().max(80).nullable().default(null), tenkh: z.string().max(200).nullable().default(null) })
      .default({ makh: null, tenkh: null }),
    savedAt: z.string().max(40).default(''),
    site: z
      .object({
        upBearingDeg: mm(0, 360).default(0),
        defaultEaveMm: mm(0, 200_000).default(6000),
        defaultPitchDeg: mm(0, 60).default(10),
        defaultFaceDeg: mm(0, 360).default(180),
        /** Site location for the sun position (shading simulation); null = not entered */
        latitude: z.number().min(-90).max(90).nullable().default(null),
        longitude: z.number().min(-180).max(180).nullable().default(null),
        /** Time zone, hours from UTC (Vietnam: +7) */
        utcOffset: z.number().min(-12).max(14).default(7),
        /** Plan point (mm) that sits at latitude / longitude (map imagery and locations are placed from it) */
        geoAnchor: pt.nullable().default(null),
      })
      .loose()
      .default({ upBearingDeg: 0, defaultEaveMm: 6000, defaultPitchDeg: 10, defaultFaceDeg: 180, latitude: null, longitude: null, utcOffset: 7, geoAnchor: null }),
    module: z
      .object({
        label: z.string().max(120).default(''),
        wp: mm(50, 1500),
        wMm: mm(300, 2000),
        hMm: mm(300, 3000),
        gapMm: mm(0, 500),
        thicknessMm: mm(1, 200).optional(),
        massKg: mm(0.1, 500).optional(),
        productId: z.string().max(60).optional(),
        manufacturer: z.string().max(120).optional(),
        model: z.string().max(120).optional(),
      })
      .loose(),
    roofs: z.array(roofSchema).max(300).default([]),
    pvZones: z.array(pvZoneSchema).max(2000).default([]),
    buildings: z.array(buildingSchema).max(100).default([]),
    obstacles: z.array(obstacleSchema).max(2000).default([]),
    structures: z.array(structureInstanceSchema).max(300).default([]),
    sections: z.array(steelSectionSchema).max(300).default([]),
    equipment: z.array(projectEquipmentSchema).max(500).default([]),
    routes: z.array(projectRouteSchema).max(500).default([]),
    om: omDataSchema.default(() => emptyOm()),
    bess: bessDataSchema.default(() => emptyBess()),
    measures: z.array(z.object({ id, a: pt, b: pt }).loose()).max(1000).default([]),
    /** Objects outside the project that cast shadows: neighbouring buildings / structures and trees */
    surroundings: z.array(surroundingSchema).max(500).default([]),
    background: backgroundSchema.nullable().default(null),
    layers: z.partialRecord(z.enum(LAYER_KEYS), z.boolean()).default({}),
  })
  .loose();
export type DesignProject = z.infer<typeof projectSchema>;

export type ProjectParse = { ok: true; project: DesignProject } | { ok: false; error: string };

/** Read a project from an untrusted source (file, autosave, server draft). */
export function parseProject(raw: unknown): ProjectParse {
  let o: unknown = raw;
  if (typeof raw === 'string') {
    try {
      o = JSON.parse(raw);
    } catch {
      return { ok: false, error: 'Tệp không phải JSON hợp lệ.' };
    }
  }
  if (!o || typeof o !== 'object') return { ok: false, error: 'Tệp dự án rỗng hoặc sai định dạng.' };
  const r0 = o as Record<string, unknown>;
  if (r0.schema !== PROJECT_SCHEMA)
    return {
      ok: false,
      error:
        r0.schema === 'S.M.A.R.T.solar-layout'
          ? 'Đây là tệp của công cụ bố trí cũ — phiên bản mới không mở tệp cũ (đã thống nhất). Hãy vẽ lại trên công cụ mới.'
          : 'Không phải tệp dự án thiết kế (schema không khớp).',
    };
  if (typeof r0.version === 'number' && r0.version > PROJECT_VERSION)
    return { ok: false, error: `Tệp phiên bản ${r0.version} mới hơn ứng dụng (hỗ trợ ${PROJECT_VERSION}).` };
  const r = projectSchema.safeParse(migrateProject(r0));
  if (!r.success) {
    const i = r.error.issues[0];
    return { ok: false, error: `Tệp dự án không hợp lệ: ${i?.path.join('.') ?? ''} ${i?.message ?? ''}`.trim() };
  }
  return { ok: true, project: r.data };
}

/**
 * Version migrations (v2 → v3 only adds fields with defaults: workspace, roof modules / mode / carport, BESS).
 * Each step returns a new object; unknown fields are kept.
 */
export function migrateProject(o: Record<string, unknown>): Record<string, unknown> {
  let x = o;
  if (x.version === 2) x = { ...x, version: 3 };
  return x;
}

export function emptyProject(idValue: string): DesignProject {
  return projectSchema.parse({
    schema: PROJECT_SCHEMA,
    version: PROJECT_VERSION,
    id: idValue,
    module: { label: 'JA', wp: 650, wMm: 1134, hMm: 2382, gapMm: 20 },
  });
}
